/**
 * BrandingContext
 * 
 * Provides branding configuration to all applicant-facing pages.
 * Fetches BrandingConfig for the current competition year and applies
 * pageant name, colors, header text, and logo.
 * 
 * Requirements: 16.4, 16.5
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { BrandingConfig } from '@/types';
import { getBrandingConfig } from '@/graphql/queries';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { resolveBrandName } from '@/lib/tenantBranding';

const client = generateClient();

export interface BrandingContextValue {
  /** Current branding configuration */
  branding: BrandingConfig | null;
  /**
   * The effective brand (pageant) name for the active surface — the active
   * tenant's own name when the viewer is resolved into a Tenant, else the
   * fetched/default pageant name (tenant-foundation Task 18, white-label). Read
   * this (not `branding?.pageantName`) wherever the org name is displayed so the
   * authenticated app reads as the active tenant.
   */
  brandName: string;
  /** Whether branding is loading */
  isLoading: boolean;
  /** Error message if branding failed to load */
  error: string | null;
  /** Current competition year */
  competitionYear: number;
  /** Refresh branding configuration */
  refresh: () => Promise<void>;
  /** Get the full logo URL from S3 key */
  getLogoUrl: (s3Key: string) => string;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export interface BrandingProviderProps {
  /** Child components */
  children: React.ReactNode;
  /** Competition year to fetch branding for */
  competitionYear?: number;
  /** S3 bucket URL for branding assets */
  brandingBucketUrl?: string;
}

/**
 * Get the current competition year
 * Uses the current year, or next year if we're past September
 */
function getCurrentCompetitionYear(): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  // If past September, use next year
  return month >= 9 ? year + 1 : year;
}

/**
 * Default branding configuration
 */
const DEFAULT_BRANDING: BrandingConfig = {
  competitionYear: getCurrentCompetitionYear(),
  pageantName: 'Idaho Pageants',
  primaryColor: '#1a365d',
  accentColor: '#c53030',
  headerText: 'Welcome to Idaho Pageants',
  updatedAt: new Date().toISOString(),
};

/**
 * BrandingProvider Component
 * 
 * Fetches and provides branding configuration to all child components.
 * Applies branding to applicant-facing pages (Requirement 16.4).
 */
export function BrandingProvider({
  children,
  competitionYear: propCompetitionYear,
  brandingBucketUrl = import.meta.env.VITE_BRANDING_BUCKET_URL || '',
}: BrandingProviderProps): React.ReactElement {
  const competitionYear = propCompetitionYear ?? getCurrentCompetitionYear();
  
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await client.graphql({
        query: getBrandingConfig,
        variables: { competitionYear },
      });

      const config = (response as { data: { getBrandingConfig: BrandingConfig | null } })
        .data.getBrandingConfig;

      if (config) {
        setBranding(config);
      } else {
        // Use default branding if none configured
        setBranding({ ...DEFAULT_BRANDING, competitionYear });
      }
    } catch (err) {
      console.error('Failed to fetch branding config:', err);
      setError('Failed to load branding configuration');
      // Fall back to default branding on error
      setBranding({ ...DEFAULT_BRANDING, competitionYear });
    } finally {
      setIsLoading(false);
    }
  }, [competitionYear]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const getLogoUrl = useCallback((s3Key: string): string => {
    if (!s3Key) return '';
    if (s3Key.startsWith('http')) return s3Key;
    return `${brandingBucketUrl}/${s3Key}`;
  }, [brandingBucketUrl]);

  // White-label (tenant-foundation Task 18): overlay the active tenant's own
  // name so the authenticated app reads as that tenant. The active tenant name
  // is the canonical `Tenant.name`, surfaced client-side as the matching
  // Membership's `tenantName` (the self-only `listMyMemberships` the switcher
  // already loads). Public / pre-auth has no active tenant → the fetched/default
  // (Idaho) name, so the public marketing site is unaffected.
  const { user } = useAuth();
  const { memberships } = useMyMemberships();
  const brandName = resolveBrandName(user?.activeTenantId, memberships, branding?.pageantName);

  // Reflect the effective brand in the browser tab title.
  useEffect(() => {
    if (brandName) document.title = brandName;
  }, [brandName]);

  const contextValue = useMemo<BrandingContextValue>(() => ({
    branding,
    brandName,
    isLoading,
    error,
    competitionYear,
    refresh: fetchBranding,
    getLogoUrl,
  }), [branding, brandName, isLoading, error, competitionYear, fetchBranding, getLogoUrl]);

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * useBranding Hook
 * 
 * Access branding configuration from any component.
 * Must be used within a BrandingProvider.
 */
export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

/**
 * CSS custom properties for branding
 * Apply these to the root element to enable branding throughout the app
 */
export function getBrandingCssVars(branding: BrandingConfig | null): React.CSSProperties {
  if (!branding) return {};
  
  return {
    '--branding-primary-color': branding.primaryColor,
    '--branding-accent-color': branding.accentColor,
    '--branding-pageant-name': `"${branding.pageantName}"`,
  } as React.CSSProperties;
}

export default BrandingContext;
